import React from 'react';
import { Box, Button, CircularProgress, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { Text } from '../../../../../../common/components/Text/Text';
import { Tabs } from '../../../../../../common/components/Tabs/Tabs';
import { Tab } from '../../../../../../common/components/Tab/Tab';
import { CEFR_LEVELS, EExerciseStatus, TExerciseChoiceItem, TExerciseMatchItem } from '../../../../../../common/rest/exercises/exercisesRest';
import { ExerciseChoice } from './ExerciseChoice';
import { ExerciseMatch } from './ExerciseMatch';
import { IExercisesTabProps } from './types';
import { isExerciseNativeFor } from '../../../../../store/exercises/selectors';
import { hasTranslatableText, isMatchContent } from '../../../../../../common/utils/exerciseContentUtil';

const noop: () => void = () => undefined;

export const ExercisesTab: React.FC<IExercisesTabProps> = ({
	audioId,
	audioLoaded,
	hasTranscript,
	langCode,
	loaded,
	loadFailed,
	types,
	exercises,
	myAnswers,
	canRegenerate,
	generatingPromptIds,
	generateFailedPromptIds,
	students,
	studentAnswers,
	nativeLangCode,
	exerciseNatives,
	onLoad,
	onLoadNative,
	onGenerate,
	onAnswer,
	onClearAnswers,
	onLoadStudents,
	onSetStatus,
	onTimestampClick,
}) => {

	// audioId stays empty until both the route and the login have resolved after a reload
	React.useEffect(() => {
		if (audioId) onLoad();
	}, [audioId]);

	// canRegenerate is false until the exercises fetch resolves, so this can't run alongside onLoad() above —
	// it re-fires once the real value comes in
	React.useEffect(() => {
		if (canRegenerate) onLoadStudents();
	}, [canRegenerate]);

	const [activePromptId, setActivePromptId] = React.useState<number | null>(null);
	const scrollRef = React.useRef<HTMLDivElement | null>(null);
	React.useEffect(() => {
		if (activePromptId === null && types.length) {
			setActivePromptId(types[0].promptId);
		}
	}, [types, activePromptId]);

	// Regenerate opens a level picker anchored here
	const [levelMenuAnchor, setLevelMenuAnchor] = React.useState<HTMLElement | null>(null);

	// null = viewing my own answers; otherwise the userId of the participant being reviewed (teacher/admin only)
	const [viewUserId, setViewUserId] = React.useState<number | null>(null);

	// opening an exercise that doesn't exist yet generates it — for everyone, so a student never has to.
	// Stops after a failure (generateFailed) until Try again, so a broken prompt can't loop.
	const autoPromptId = activePromptId ?? types[0]?.promptId;
	const autoMissing = autoPromptId !== undefined && !exercises.some(e => e.promptId === autoPromptId);
	const autoBlocked = autoPromptId !== undefined
		&& (generatingPromptIds.includes(autoPromptId) || generateFailedPromptIds.includes(autoPromptId));
	React.useEffect(() => {
		if (loaded && hasTranscript && autoMissing && !autoBlocked) onGenerate(autoPromptId!);
	}, [loaded, hasTranscript, autoPromptId, autoMissing, autoBlocked]);

	// translation into the viewer's native language — of the open exercise, once per content; none in the lesson language
	React.useEffect(() => {
		const ex = exercises.find(e => e.promptId === autoPromptId);
		if (!ex || ex.status === EExerciseStatus.INACTIVE || !hasTranslatableText(ex.content)) return;
		if (!nativeLangCode || !langCode || nativeLangCode === langCode) return;
		if (!isExerciseNativeFor(exerciseNatives[ex.id], ex.content, nativeLangCode)) onLoadNative(ex.id);
	}, [exercises, autoPromptId, nativeLangCode, langCode, exerciseNatives]);

	if (!audioLoaded) {
		return (
			<Stack alignItems={'center'} justifyContent={'center'} sx={{ flex: '1 1 auto', minHeight: 0 }}>
				<CircularProgress size={theme.spacing(4)} />
			</Stack>
		);
	}

	if (!hasTranscript) {
		return (
			<Stack alignItems={'center'} justifyContent={'center'} sx={{ flex: '1 1 auto', minHeight: 0 }}>
				<Text sx={{ fontSize: theme.typography.subtitle1, color: theme.palette.grey[400] }}>
					Exercises can be generated once the recording has a transcript
				</Text>
			</Stack>
		);
	}

	if (!loaded && loadFailed) {
		return (
			<Stack alignItems={'center'} justifyContent={'center'} gap={1.5} sx={{ flex: '1 1 auto', minHeight: 0 }}>
				<Text sx={{ color: theme.palette.grey[400] }}>Failed to load exercises</Text>
				<Button onClick={onLoad}>Try again</Button>
			</Stack>
		);
	}

	if (!loaded) {
		return (
			<Stack alignItems={'center'} justifyContent={'center'} sx={{ flex: '1 1 auto', minHeight: 0 }}>
				<CircularProgress size={theme.spacing(4)} />
			</Stack>
		);
	}

	if (!types.length) {
		return null;
	}

	const activeType = types.find(t => t.promptId === activePromptId) || types[0];
	const exercise = exercises.find(e => e.promptId === activeType.promptId);
	const isGenerating = generatingPromptIds.includes(activeType.promptId);
	const generateFailed = generateFailedPromptIds.includes(activeType.promptId);
	// only a teacher/admin ever receives an inactive exercise — students get neither it nor its type
	const isInactive = exercise?.status === EExerciseStatus.INACTIVE;
	// the saved exercise's own format, not the prompt's — an admin may change the prompt's format later,
	// and rendering old content with the other component crashes. Older rows: tell by the item shape.
	const exerciseFormat = exercise && isMatchContent(exercise.content) ? 'match' : 'choice';
	const isViewingStudent = viewUserId !== null;
	const nativeState = exercise ? exerciseNatives[exercise.id] : undefined;
	const nativeRequested = !!exercise && isExerciseNativeFor(nativeState, exercise.content, nativeLangCode);
	const native = nativeRequested ? nativeState?.data || undefined : undefined;
	const nativeLoading = nativeRequested && nativeState?.data === null;

	// answers of whoever is being viewed (me, or the selected student)
	const answersOf = (exerciseId: number) => isViewingStudent
		? studentAnswers.filter(a => a.exerciseId === exerciseId && a.userId === viewUserId)
		: myAnswers.filter(a => a.exerciseId === exerciseId);

	// per exercise type: done = every question answered; not generated yet counts as not done
	const progress = types.map(t => {
		const ex = exercises.find(e => e.promptId === t.promptId);
		const total = ex?.content.items.length || 0;
		const answered = ex ? new Set(answersOf(ex.id).map(a => a.questionIndex)).size : 0;
		// skipped: taken out by the teacher, or generated empty (nothing to practise in this lesson)
		const inactive = ex?.status === EExerciseStatus.INACTIVE || (!!ex && !total);
		return { promptId: t.promptId, answered, total, inactive, done: !inactive && !!total && answered >= total };
	});
	const activeProgress = progress.find(p => p.promptId === activeType.promptId);
	const allDone = progress.some(p => !p.inactive) && progress.every(p => p.done || p.inactive);
	const activeIndex = types.indexOf(activeType);
	// the next unfinished (and not removed) exercise after this one, wrapping around
	const nextType = [...types.slice(activeIndex + 1), ...types.slice(0, activeIndex)]
		.find(t => {
			const p = progress.find(x => x.promptId === t.promptId);
			return !p?.done && !p?.inactive;
		});

	const openType = (promptId: number) => {
		setActivePromptId(promptId);
		scrollRef.current?.scrollTo({ top: 0 });
	};

	const answersByQuestion = exercise
		? Object.fromEntries(answersOf(exercise.id).map(a => [a.questionIndex, a]))
		: {};
	const answeredCount = Object.keys(answersByQuestion).length;
	const correctCount = Object.values(answersByQuestion).filter(a => a.correct).length;
	// a match set can only be finished with every pair right, so "N correct" would just repeat "answered"
	const showCorrect = !!answeredCount && exerciseFormat !== 'match';
	const totalCount = exercise?.content.items.length || 0;
	const viewedStudentName = isViewingStudent ? students.find(s => s.userId === viewUserId)?.name : undefined;

	const buttonSx = {
		fontSize: theme.typography.subtitle1,
	}

	return (
		<Stack ref={scrollRef} gap={1.5} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
			{canRegenerate && !!students.length &&
			<Stack direction={'row'} sx={{ flexWrap: 'wrap' }} gap={.75}>
				{[{ userId: null, name: 'My answers' }, ...students.map(s => ({ userId: s.userId as number | null, name: s.name }))].map(entry => (
					<Box
						key={entry.userId ?? 'me'}
						component={'button'}
						onClick={() => setViewUserId(entry.userId)}
						sx={{
							border: 'none',
							borderRadius: theme.spacing(2),
							background: viewUserId === entry.userId ? theme.palette.primary.main : theme.palette.grey[100],
							color: viewUserId === entry.userId ? theme.palette.common.white : theme.palette.grey[700],
							p: theme.spacing(.5, 1.25),
							cursor: 'pointer',
							fontSize: theme.typography.subtitle2,
						}}
					>
						{entry.name}
					</Box>
				))}
			</Stack>
			}

			<Stack direction={'row'} alignItems={'center'} gap={1} sx={{ flexShrink: 0, maxWidth: 1 }}>
			<Tabs
				value={activeType.promptId}
				onChange={(_, promptId) => openType(promptId)}
				sx={{
					alignSelf: 'flex-start',
					maxWidth: 1,
					flexShrink: 0,
					// one sub-tab per exercise prompt, so there can be many — wrap instead of clipping
					// overflow visible: the active tab's underline sits just below it
					'.MuiTabs-scroller': { whiteSpace: 'normal', overflow: 'visible !important' },
					'.MuiTabs-flexContainer': { flexWrap: 'wrap', justifyContent: 'flex-start' },
				}}
			>
				{types.map((t, i) => {
					const p = progress[i];
					const exerciseOfTab = exercises.find(e => e.promptId === t.promptId);
					const isRemoved = exerciseOfTab?.status === EExerciseStatus.INACTIVE;
					// grey button: not generated yet (black text) or generated empty — "Nothing to practise" (paler text);
					// removed: grey text, struck out
					const isGeneratedEmpty = p.inactive && !isRemoved;
					const isEmpty = !exerciseOfTab || isGeneratedEmpty;
					const background = p.done ? theme.palette.success.main + '33' : isEmpty ? theme.palette.grey[100] : theme.palette.common.white;
					return (
						<Tab
							key={t.promptId}
							value={t.promptId}
							// Tooltip on the label: wrapping the Tab itself breaks Tabs' value matching
							label={
								<Tooltip title={t.title} placement={'top'} disableInteractive>
									<Box component={'span'} sx={{ display: 'block', width: 1, textAlign: 'center' }}>{i + 1}</Box>
								</Tooltip>
							}
							sx={{
								width: theme.spacing(3.5),
								px: 0, // the shared Tab's 16px sides leave no room in 28px and push the number off-centre
								overflow: 'visible',
								color: `${p.done
									? theme.palette.success.dark
									: isRemoved || isGeneratedEmpty
										? theme.palette.grey[400]
										: theme.palette.grey[900]} !important`,
								textDecoration: isRemoved ? 'line-through' : 'none',
								background,

								'&:hover': { background: p.done ? theme.palette.success.main + '55' : isEmpty ? theme.palette.grey[200] : theme.palette.grey[50] },
								'&.Mui-selected': {
									background,
									boxShadow: 'none',
									'&:after': {
										content: '""',
										position: 'absolute',
										left: theme.spacing(.5),
										right: theme.spacing(.5),
										bottom: theme.spacing(-.5),
										height: '3px',
										borderRadius: '2px',
										background: p.done ? theme.palette.success.main : theme.palette.primary.main,
									},
								},
							}}
						/>
					);
				})}
			</Tabs>
			{allDone &&
				<Tooltip title={'All exercises done'}>
					<Box component={'span'} sx={{ fontSize: theme.spacing(2.5) }}>👌</Box>
				</Tooltip>
			}
			</Stack>

			<Stack
				sx={{
					pl: theme.spacing(1),
				}}
			>
				{/* baseline: the smaller level text sits on the title's line, not centred against it */}
				<Stack direction={'row'} alignItems={'baseline'} gap={.75} sx={{ flexShrink: 0 }}>
					<Text
						sx={{
							fontSize: theme.typography.h6,
						}}
					>{activeType.title}</Text>
					{exercise?.content.level &&
					<Tooltip title={'Exercise level (CEFR)'}>
						<Box
							component={'span'}
							sx={{
								px: .5,
								color: theme.palette.grey[500],
								fontSize: theme.typography.subtitle1,
							}}
						>
							{exercise.content.level}
						</Box>
					</Tooltip>
					}
					{nativeLoading &&
					<Tooltip title={'Translating…'}>
						<CircularProgress size={theme.spacing(1.5)} sx={{ alignSelf: 'center' }} />
					</Tooltip>
					}
				</Stack>

				{exercise && !isInactive &&
					<Stack
						direction={'row'}
						alignItems={'center'}
						gap={1.5}
						rowGap={.5}
						sx={{ flexWrap: 'wrap' }}
					>
						<Text sx={{ color: theme.palette.grey[500] }}>
							{`${isViewingStudent ? `${viewedStudentName}: ` : ''}${answeredCount} / ${totalCount} answered${showCorrect ? ` · ${correctCount} correct` : ''}`}
						</Text>

						<Stack
							direction={'row'}
							alignItems={'center'}
							gap={1.5}
							sx={{ flexWrap: 'wrap' }}
						>
							{canRegenerate && !!answeredCount && !isViewingStudent &&
							<Button
								size={'small'}
								sx={buttonSx}
								onClick={() => onClearAnswers(exercise.id)}
							>Clear answers</Button>
							}
							{canRegenerate && !!answeredCount && isViewingStudent &&
							<Button
								size={'small'}
								sx={buttonSx}
								onClick={() => {
									if (window.confirm(`Reset ${viewedStudentName}'s answers to this exercise? They'll be able to do it again.`)) {
										onClearAnswers(exercise.id, viewUserId!);
									}
								}}
							>
								Reset answers
							</Button>
							}
							{canRegenerate &&
							<Button
								size={'small'}
								sx={buttonSx}
								disabled={isGenerating}
								onClick={(e) => setLevelMenuAnchor(e.currentTarget)}
							>
								{isGenerating ? 'Generating…' : 'Regenerate'}
							</Button>
							}
							{canRegenerate &&
							<Menu anchorEl={levelMenuAnchor} open={!!levelMenuAnchor} onClose={() => setLevelMenuAnchor(null)}>
								{/* no "auto": the current level is marked; older exercises without one mark nothing */}
								{CEFR_LEVELS.map(level => (
									<MenuItem
										key={level}
										selected={level === exercise.content.level}
										onClick={() => {
											setLevelMenuAnchor(null);
											if (window.confirm('Regenerate this exercise? Every student\'s answers will be cleared.')) {
												onGenerate(activeType.promptId, level);
											}
										}}
									>
										{level}
									</MenuItem>
								))}
							</Menu>
							}
							{canRegenerate &&
							<Button
								size={'small'}
								sx={{
									...buttonSx,
									color: theme.palette.error.main,
								}}
								onClick={() => {
									if (window.confirm('Remove this exercise from the lesson? Students will no longer see it. You can restore it later.')) {
										onSetStatus(exercise.id, EExerciseStatus.INACTIVE);
									}
								}}
							>
								Remove
							</Button>
							}
						</Stack>
					</Stack>
				}
			</Stack>

			{exercise && isInactive &&
			<Stack alignItems={'center'} justifyContent={'center'} gap={1.5} sx={{ flex: '1 1 auto', p: theme.spacing(4) }}>
				<Text sx={{ color: theme.palette.grey[400] }}>Removed from this lesson — students don't see it</Text>
				<Button variant={'contained'} onClick={() => onSetStatus(exercise.id, EExerciseStatus.ACTIVE)}>Restore</Button>
			</Stack>
			}

			{exercise && !isInactive && !exercise.content.items.length &&
			<Stack alignItems={'center'} justifyContent={'center'} sx={{ flex: '1 1 auto', p: theme.spacing(4) }}>
				<Text sx={{ color: theme.palette.grey[400] }}>Nothing to practise here for this lesson</Text>
			</Stack>
			}

			{exercise && !isInactive && !!exercise.content.items.length &&
			<>
				{exerciseFormat === 'match'
					? <ExerciseMatch
						instruction={exercise.content.instruction}
						instructionNative={native?.instruction}
						setsNative={native?.sets}
						items={exercise.content.items as TExerciseMatchItem[]}
						answers={answersByQuestion}
						langCode={langCode}
						readOnly={isViewingStudent}
						viewKey={isViewingStudent ? `student:${viewUserId}` : 'me'}
						onAnswer={isViewingStudent ? noop : (questionIndex, order) => onAnswer(exercise.id, questionIndex, { order })}
					/>
					: <ExerciseChoice
						items={exercise.content.items as TExerciseChoiceItem[]}
						answers={answersByQuestion}
						langCode={langCode}
						readOnly={isViewingStudent}
						highlightDiff={activeType.key === 'ex_error_correction'}
						native={native?.items}
						onAnswer={isViewingStudent ? noop : (questionIndex, choice) => onAnswer(exercise.id, questionIndex, { choice })}
						onTimestampClick={onTimestampClick}
					/>
				}

				{activeProgress?.done &&
				<Stack direction={'row'} alignItems={'center'} justifyContent={'center'} gap={1.5} sx={{ py: 1 }}>
					{nextType
						? <Button variant={'contained'} onClick={() => openType(nextType.promptId)}>Next exercise</Button>
						: <Text sx={{ color: theme.palette.success.main, fontWeight: 600 }}>All exercises done 👌</Text>
					}
				</Stack>
				}
			</>
			}

			{/* not generated yet: auto-generation (above) is already running, or it failed */}
			{!exercise &&
			<Stack alignItems={'center'} justifyContent={'center'} gap={1.5} sx={{ flex: '1 1 auto', p: theme.spacing(4) }}>
				{generateFailed
					? <>
						<Text sx={{ color: theme.palette.grey[400] }}>Failed to prepare the exercise</Text>
						<Button onClick={() => onGenerate(activeType.promptId)}>Try again</Button>
					</>
					: <>
						<CircularProgress size={theme.spacing(4)} />
						<Text sx={{ color: theme.palette.grey[400] }}>Preparing exercise…</Text>
					</>
				}
			</Stack>
			}

		</Stack>
	);
};
